import { create } from 'zustand';
import {
  DEFAULT_COHORT,
  DEFAULT_EXCLUSION_POPULATION,
  DEFAULT_PARAMS,
  DEFAULT_SUBPOPULATION,
} from '@/model/defaults';
import { clamp, mergeParams } from '@/model/funnel';
import type {
  ExclusionPopulation,
  FollowupCohort,
  FunnelParams,
  FunnelParamsPatch,
  ScreeningOutcomes,
  Subpopulation,
} from '@/model/types';
import type { StageId, ViewId } from '@/layout/types';
import { newId } from '@/lib/id';

/** What the impact boxes compare against: the moment the drawer opened. */
export interface Baseline {
  completed: number;
  screeningRate: number;
}

export type SaveStatus = 'idle' | 'loading' | 'saving' | 'saved' | 'error';

interface FunnelState {
  params: FunnelParams;
  view: ViewId;
  selectedStage: StageId | null;
  baseline: Baseline | null;
  status: SaveStatus;
  statusMessage: string | null;
  /** Bumped on every params change; the autosave hook watches this. */
  revision: number;
  /**
   * The change-history panel. A peer of the stage drawer, never a third layer
   * over it — see `openHistory`.
   */
  historyOpen: boolean;
  /**
   * Saves have started failing: the scenario opened, then the service went away.
   *
   * This exists to be *shown*. Silently not saving is the failure mode that
   * matters most here — the canvas looks completely normal while a session's
   * edits go nowhere but localStorage.
   */
  degraded: boolean;

  hydrate: (patch: FunnelParamsPatch | null) => void;
  resetEditSession: () => void;
  setStatus: (status: SaveStatus, message?: string | null) => void;

  setOutcome: (key: keyof ScreeningOutcomes, share: number) => void;

  addSubpopulation: () => void;
  updateSubpopulation: (id: string, patch: Partial<Omit<Subpopulation, 'id'>>) => void;
  removeSubpopulation: (id: string) => void;

  addExclusionPopulation: () => void;
  updateExclusionPopulation: (
    id: string,
    patch: Partial<Omit<ExclusionPopulation, 'id'>>,
  ) => void;
  removeExclusionPopulation: (id: string) => void;

  addCohort: () => void;
  updateCohort: (id: string, patch: Partial<Omit<FollowupCohort, 'id'>>) => void;
  removeCohort: (id: string) => void;

  resetParams: () => void;

  setView: (view: ViewId) => void;
  openStage: (stage: StageId, baseline: Baseline) => void;
  closeDrawer: () => void;

  openHistory: () => void;
  closeHistory: () => void;

  setDegraded: (degraded: boolean) => void;
}

/** The data half of the store, so `pristine()` has exactly one definition. */
function pristine() {
  return {
    params: structuredClone(DEFAULT_PARAMS),
    view: 'main' as ViewId,
    selectedStage: null,
    baseline: null,
    status: 'idle' as SaveStatus,
    statusMessage: null,
    revision: 0,
    historyOpen: false,
    degraded: false,
  };
}

export const useFunnelStore = create<FunnelState>()((set) => {
  /** Every params mutation goes through here so `revision` cannot drift. */
  const mutate = (fn: (params: FunnelParams) => FunnelParams) =>
    set((state) => ({ params: fn(state.params), revision: state.revision + 1 }));

  return {
    ...pristine(),

    hydrate: (patch) =>
      set((state) => ({
        params: mergeParams(DEFAULT_PARAMS, patch),
        status: 'idle',
        statusMessage: null,
        // A load is not an edit; do not trigger an immediate save-back.
        revision: state.revision,
      })),

    /**
     * Back to a pristine store: no params, no revision, no panels, no degraded
     * flag. Call it BEFORE loading a scenario.
     *
     * This exists because the store is a module singleton and `revision` is the
     * only thing standing between a freshly opened scenario and an autosave.
     * Open A, edit it (revision > 0), then open B: `hydrate` deliberately
     * preserves `revision`, so B arrives with a non-zero one and `useAutosave`
     * writes B's just-loaded params straight back — forging an `update` revision
     * in the log of a scenario nobody edited. In the admin console, merely
     * LOOKING at eight scenarios would forge eight entries.
     *
     * Distinct from `resetParams`, which is a user action ("reset to defaults")
     * and therefore an edit that MUST bump `revision` and MUST be saved. This is
     * the opposite: not an edit, but the absence of one.
     *
     * It does not contradict `hydrate` either. `hydrate` says "a load is not an
     * edit" and so leaves `revision` alone; this says "there is no edit session
     * at all" and so zeroes it.
     *
     * NEVER call this from an effect cleanup. Cleanups run in hook-declaration
     * order, so a reset in the load hook's cleanup would set `params` back to
     * DEFAULT_PARAMS and then `useAutosave`'s unmount flush — which reads
     * `params` straight from this store — would beacon the defaults over
     * whatever was just edited.
     */
    resetEditSession: () => set(pristine()),

    setStatus: (status, message = null) => set({ status, statusMessage: message }),

    setOutcome: (key, share) =>
      mutate((params) => ({
        ...params,
        outcomes: { ...params.outcomes, [key]: Math.max(0, share) },
      })),

    addSubpopulation: () =>
      mutate((params) => ({
        ...params,
        subpopulations: [
          ...params.subpopulations,
          { id: newId('sub'), ...DEFAULT_SUBPOPULATION },
        ],
      })),

    updateSubpopulation: (id, patch) =>
      mutate((params) => ({
        ...params,
        subpopulations: params.subpopulations.map((s) =>
          s.id === id
            ? {
                ...s,
                ...patch,
                ...(patch.count === undefined ? {} : { count: Math.max(0, patch.count) }),
                ...(patch.reach === undefined ? {} : { reach: clamp(patch.reach, 0, 1) }),
                ...(patch.screenRate === undefined
                  ? {}
                  : { screenRate: clamp(patch.screenRate, 0, 1) }),
              }
            : s,
        ),
      })),

    removeSubpopulation: (id) =>
      mutate((params) => ({
        ...params,
        subpopulations: params.subpopulations.filter((s) => s.id !== id),
      })),

    addExclusionPopulation: () =>
      mutate((params) => ({
        ...params,
        exclusionPopulations: [
          ...params.exclusionPopulations,
          { id: newId('exclusion'), ...DEFAULT_EXCLUSION_POPULATION },
        ],
      })),

    updateExclusionPopulation: (id, patch) =>
      mutate((params) => ({
        ...params,
        exclusionPopulations: params.exclusionPopulations.map((exclusion) =>
          exclusion.id === id
            ? {
                ...exclusion,
                ...patch,
                ...(patch.count === undefined ? {} : { count: Math.max(0, patch.count) }),
              }
            : exclusion,
        ),
      })),

    removeExclusionPopulation: (id) =>
      mutate((params) => ({
        ...params,
        exclusionPopulations: params.exclusionPopulations.filter(
          (exclusion) => exclusion.id !== id,
        ),
      })),

    addCohort: () =>
      mutate((params) => ({
        ...params,
        followupCohorts: [...params.followupCohorts, { id: newId('cohort'), ...DEFAULT_COHORT }],
      })),

    updateCohort: (id, patch) =>
      mutate((params) => ({
        ...params,
        followupCohorts: params.followupCohorts.map((c) =>
          c.id === id
            ? {
                ...c,
                ...patch,
                ...(patch.share === undefined ? {} : { share: Math.max(0, patch.share) }),
                ...(patch.reach === undefined ? {} : { reach: clamp(patch.reach, 0, 1) }),
                ...(patch.completion === undefined
                  ? {}
                  : { completion: clamp(patch.completion, 0, 1) }),
              }
            : c,
        ),
      })),

    removeCohort: (id) =>
      mutate((params) => ({
        ...params,
        followupCohorts: params.followupCohorts.filter((c) => c.id !== id),
      })),

    resetParams: () => mutate(() => structuredClone(DEFAULT_PARAMS)),

    setView: (view) => set({ view }),

    openStage: (stage, baseline) =>
      set((state) => ({
        selectedStage: stage,
        // Keep the original baseline when moving between stages in one session.
        baseline: state.selectedStage === null ? baseline : state.baseline,
        // The drawer and the history panel are peers, not stacked layers: only
        // one right-hand panel is ever open. That keeps Esc precedence exactly
        // two deep (drawer, then modal) instead of three.
        historyOpen: false,
      })),

    closeDrawer: () => set({ selectedStage: null, baseline: null }),

    openHistory: () => set({ historyOpen: true, selectedStage: null, baseline: null }),

    closeHistory: () => set({ historyOpen: false }),

    setDegraded: (degraded) => set({ degraded }),
  };
});
